import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { BaseMultiLanguageConfiguration } from './ConfigurationManagerService';
import { buildBusinessStateSignature } from 'src/app/helpers/configuration-state-normalizer';
import { EditorNavigationContextService, NavigationContext } from './EditorNavigationContextService';

/**
 * Préfixe commun des logs de navigation post-undo/redo.
 * Activable en runtime via `localStorage.setItem('history.navLogs', '1')`.
 */
export const HISTORY_NAV_LOG_PREFIX = '[HistoryNav]';

/**
 * Indique si les logs `[HistoryNav]` doivent être émis.
 * Lecture défensive (pas d'accès `localStorage` côté SSR/test).
 */
export function isHistoryNavLoggingEnabled(): boolean {
  try {
    return typeof localStorage !== 'undefined'
      && localStorage.getItem('history.navLogs') === '1';
  } catch {
    return false;
  }
}

/**
 * Helper de log conditionnel pour les opérations de navigation post-undo/redo.
 * No-op si le flag `history.navLogs` n'est pas activé dans le `localStorage`.
 */
export function historyNavLog(message: string, payload?: unknown): void {
  if (!isHistoryNavLoggingEnabled()) {
    return;
  }
  if (payload !== undefined) {
    console.log(`${HISTORY_NAV_LOG_PREFIX} ${message}`, payload);
  } else {
    console.log(`${HISTORY_NAV_LOG_PREFIX} ${message}`);
  }
}

/**
 * Interface représentant une entrée dans l'historique des modifications
 */
export interface HistoryEntry {
  /** Identifiant unique de l'entrée */
  id: string;
  /** Timestamp de création de l'entrée */
  timestamp: number;
  /** Configuration complète à ce moment de l'historique */
  configuration: BaseMultiLanguageConfiguration;
  /** Description lisible de la modification */
  description: string;
  /** Type de l'entrée (automatique, manuelle, initiale ou batch) */
  type: 'auto' | 'manual' | 'initial' | 'batch';
  /** Taille en octets du snapshot (pour monitoring) */
  snapshotSize: number;
  /**
   * Contexte d'édition au moment de la capture (step + item sélectionné).
   * Utilisé par le configurateur pour re-naviguer vers la bonne page d'édition
   * lors d'un undo/redo. `undefined` pour les entrées de bootstrap (`initial`)
   * ou si aucun contexte n'a été enregistré.
   */
  navigationHint?: NavigationContext;
}

/**
 * Interface représentant l'état actuel de l'historique
 */
export interface HistoryState {
  /** Liste de toutes les entrées d'historique */
  entries: HistoryEntry[];
  /** Index de l'entrée actuellement active */
  currentIndex: number;
  /** Taille maximale de l'historique */
  maxHistorySize: number;
  /** Indique si une opération d'annulation est possible */
  canUndo: boolean;
  /** Indique si une opération de rétablissement est possible */
  canRedo: boolean;
}

/**
 * Contexte optionnel pour générer des descriptions d'historique lisibles.
 * Permet de passer des informations sur la modification effectuée.
 */
export interface HistoryChangeContext {
  /** Type d'opération effectuée */
  action?: 'add' | 'remove' | 'update' | 'reorder';
  /** Type d'entité modifiée */
  entityType?: 'code' | 'game' | 'quiz' | 'question' | 'structure' | 'language';
  /** Identifiant de l'entité (code du QR, nom du jeu, etc.) */
  entityId?: string;
  /** Champ modifié (hint, location, etc.) */
  field?: string;
  /** Langue de la modification */
  language?: string;
}

/**
 * Service de gestion de l'historique des modifications avec undo/redo.
 *
 * Fonctionnalités :
 * - Historique limité (50 entrées par défaut) avec navigation bidirectionnelle
 * - Déduplication : n'ajoute pas si la configuration est identique à la dernière entrée
 * - Descriptions lisibles contextuelles (ex: "Ajout du code AJ614", "Modification indice EE632")
 * - Protection anti-spam (200ms minimum entre opérations)
 * - Support des opérations groupées (batch)
 * - Flag fromHistory pour éviter les boucles infinies avec l'auto-save
 */
@Injectable({
  providedIn: 'root'
})
export class HistoryManagerService {
  /** BehaviorSubject contenant l'état actuel de l'historique */
  private _historyState = new BehaviorSubject<HistoryState>({
    entries: [],
    currentIndex: -1,
    maxHistorySize: 50,
    canUndo: false,
    canRedo: false
  });

  /** Taille maximale de l'historique */
  private _maxHistorySize = 50;

  /** Timestamp de la dernière opération d'historique (anti-spam) */
  private _lastHistoryOperation = 0;

  /** Délai anti-spam en ms */
  private readonly ANTI_SPAM_DELAY = 200;

  /** Flag indiquant qu'une opération d'historique est en cours (undo/redo) */
  private _fromHistory = false;

  /** Timer pour réinitialiser le flag fromHistory */
  private _fromHistoryTimer?: any;

  /** Flag pour les opérations groupées (batch) */
  private _isBatchOperation = false;

  /** Snapshot de début de batch pour détecter les changements */
  private _batchStartSnapshot: string | null = null;

  /**
   * Hint de navigation associé à la dernière opération undo/redo appliquée.
   *
   * Contrairement à `getCurrentEntry().navigationHint` (qui pointe sur l'état
   * RESTAURÉ), ce champ contient le hint de l'entrée DONT le changement est en
   * train d'être annulé ou rejoué — c'est-à-dire l'endroit où était
   * l'utilisateur au moment où la modification concernée a été effectuée.
   * C'est ce hint qui doit guider la navigation post-undo/redo.
   */
  private _lastAppliedNavigationHint: NavigationContext | null = null;

  constructor(
    private readonly navigationContext: EditorNavigationContextService
  ) {}

  // ─── Observables & Getters ────────────────────────────────

  /**
   * Observable de l'état de l'historique
   */
  get historyState$(): Observable<HistoryState> {
    return this._historyState.asObservable();
  }

  /**
   * État actuel de l'historique
   */
  get historyState(): HistoryState {
    return this._historyState.value;
  }

  /**
   * Indique si une opération d'annulation est possible
   */
  get canUndo(): boolean {
    return this.historyState.canUndo;
  }

  /**
   * Indique si une opération de rétablissement est possible
   */
  get canRedo(): boolean {
    return this.historyState.canRedo;
  }

  /**
   * Indique qu'une opération d'historique (undo/redo) est en cours.
   * Utilisé par l'auto-save pour ne pas recréer de snapshot.
   */
  get fromHistory(): boolean {
    return this._fromHistory;
  }

  /**
   * Hint de navigation associé à la dernière opération undo/redo appliquée.
   * Le configurateur l'utilise pour re-naviguer vers la zone d'édition où
   * la modification concernée avait eu lieu. `null` si l'opération n'a
   * capturé aucun contexte (typiquement un retour à l'état initial).
   */
  get lastAppliedNavigationHint(): NavigationContext | null {
    return this._lastAppliedNavigationHint;
  }

  // ─── Ajout à l'historique ─────────────────────────────────

  /**
   * Ajoute une nouvelle entrée à l'historique.
   * Gère la déduplication, la troncature, et les opérations groupées.
   *
   * @param configuration Configuration à sauvegarder
   * @param description Description optionnelle de la modification
   * @param type Type d'entrée (auto, manual, initial, batch)
   */
  addToHistory(
    configuration: any, 
    description?: string, 
    type: 'auto' | 'manual' | 'initial' | 'batch' = 'auto'
  ): void {
    // Si une opération d'historique est en cours, ne pas ajouter
    if (this._fromHistory) {
      console.log('Ajout ignoré (opération d\'historique en cours)');
      return;
    }

    // Si un batch est en cours, ne pas ajouter (sera fait à la fin du batch)
    if (this._isBatchOperation) {
      console.log('Ajout différé (batch en cours)');
      return;
    }

    const configJson = JSON.stringify(configuration);
    const businessStateSnapshot = this.createBusinessStateSnapshot(configuration);

    // Déduplication : ne pas ajouter si identique à la dernière entrée
    const current = this.historyState;
    if (current.currentIndex >= 0) {
      const lastEntry = current.entries[current.currentIndex];
      if (lastEntry && this.createBusinessStateSnapshot(lastEntry.configuration) === businessStateSnapshot) {
        console.log('Ajout ignoré (configuration identique)');
        return;
      }
    }
    
    // Capture du contexte d'édition uniquement pour les opérations utilisateur.
    // Les snapshots `initial` (bootstrap) doivent rester sans hint pour ne pas
    // forcer une navigation au tout premier undo qui ramène à l'état initial.
    const navigationHint = type === 'initial'
      ? undefined
      : this.navigationContext.getSnapshot() ?? undefined;

    const entry: HistoryEntry = {
      id: this.generateId(),
      timestamp: Date.now(),
      configuration: this.deepClone(configuration),
      description: description || this.generateDescription(configuration, type),
      type,
      snapshotSize: configJson.length,
      navigationHint,
    };

    historyNavLog('addToHistory: capture', {
      type,
      stepKey: navigationHint?.stepKey ?? null,
      selectedItemId: navigationHint?.selectedItemId ?? null,
      entityType: navigationHint?.entityType ?? null,
    });

    // Si on n'est pas à la fin de l'historique, supprimer les entrées après l'index actuel
    let entries = [...current.entries];
    if (current.currentIndex < entries.length - 1) {
      entries = entries.slice(0, current.currentIndex + 1);
    }

    entries.push(entry);

    // Limiter la taille de l'historique
    if (entries.length > this._maxHistorySize) {
      entries = entries.slice(-this._maxHistorySize);
    }

    const newIndex = entries.length - 1;

    this.updateHistoryState({
      entries,
      currentIndex: newIndex,
      canUndo: newIndex > 0,
      canRedo: false
    });

    console.log(`Historique: ${entry.description} (${entries.length}/${this._maxHistorySize})`);
  }

  /**
   * Ajoute une entrée avec un contexte de modification pour une description automatique.
   * @param configuration Configuration à sauvegarder
   * @param context Contexte décrivant la modification effectuée
   */
  addToHistoryWithContext(configuration: any, context: HistoryChangeContext): void {
    const description = this.generateContextDescription(context);
    this.addToHistory(configuration, description);
  }

  // ─── Opérations groupées (batch) ──────────────────────────

  /**
   * Démarre une opération groupée. Tous les addToHistory() sont ignorés
   * jusqu'à ce que endBatchOperation() soit appelé.
   * @param config Configuration de départ du batch
   */
  startBatchOperation(config?: any): void {
    this._isBatchOperation = true;
    this._batchStartSnapshot = config ? this.createBusinessStateSnapshot(config) : null;
  }

  /**
   * Termine une opération groupée et ajoute un seul snapshot à l'historique.
   * @param configuration Configuration finale après le batch
   * @param description Description de l'opération groupée
   */
  endBatchOperation(configuration: any, description?: string): void {
    this._isBatchOperation = false;

    // Vérifier si la config a réellement changé
    const businessStateSnapshot = this.createBusinessStateSnapshot(configuration);
    if (this._batchStartSnapshot && this._batchStartSnapshot === businessStateSnapshot) {
      console.log('Batch terminé sans changement');
      this._batchStartSnapshot = null;
      return;
    }

    this._batchStartSnapshot = null;
    this.addToHistory(configuration, description || 'Opération groupée', 'batch');
  }

  /**
   * Annule une opération groupée en cours sans créer de snapshot.
   */
  cancelBatchOperation(): void {
    this._isBatchOperation = false;
    this._batchStartSnapshot = null;
  }

  private createBusinessStateSnapshot(configuration: unknown): string {
    return buildBusinessStateSignature(configuration);
  }

  // ─── Undo / Redo ──────────────────────────────────────────

  /**
   * Effectue une opération d'annulation (undo) avec protection anti-spam.
   * Active le flag fromHistory pendant l'opération.
   * @returns Configuration précédente ou null si aucune annulation possible
   */
  undo(): BaseMultiLanguageConfiguration | null {
    // Protection anti-spam
    const now = Date.now();
    if (now - this._lastHistoryOperation < this.ANTI_SPAM_DELAY) {
      return null;
    }
    this._lastHistoryOperation = now;

    const current = this.historyState;
    if (!current.canUndo || current.currentIndex <= 0) {
      return null;
    }

    this.setFromHistory(true);

    // Le hint pertinent pour un undo est celui de l'entrée DONT le changement
    // est en train d'être annulé (l'entrée courante avant le décalage), pas
    // celui de l'entrée restaurée — on veut ramener l'utilisateur à l'endroit
    // où il était quand la modif a été faite.
    const undoneEntry = current.entries[current.currentIndex];
    this._lastAppliedNavigationHint = undoneEntry?.navigationHint ?? null;

    const newIndex = current.currentIndex - 1;
    const previousEntry = current.entries[newIndex];

    this.updateHistoryState({
      currentIndex: newIndex,
      canUndo: newIndex > 0,
      canRedo: true
    });

    console.log(`Undo: "${previousEntry.description}"`);
    historyNavLog('undo: restored entry', {
      id: previousEntry.id,
      hint: previousEntry.navigationHint ?? null,
    });
    return this.deepClone(previousEntry.configuration);
  }

  /**
   * Effectue une opération de rétablissement (redo) avec protection anti-spam.
   * Active le flag fromHistory pendant l'opération.
   * @returns Configuration suivante ou null si aucun rétablissement possible
   */
  redo(): BaseMultiLanguageConfiguration | null {
    // Protection anti-spam
    const now = Date.now();
    if (now - this._lastHistoryOperation < this.ANTI_SPAM_DELAY) {
      return null;
    }
    this._lastHistoryOperation = now;

    const current = this.historyState;
    if (!current.canRedo || current.currentIndex >= current.entries.length - 1) {
      return null;
    }

    this.setFromHistory(true);

    const newIndex = current.currentIndex + 1;
    const nextEntry = current.entries[newIndex];

    // Le hint pertinent pour un redo est celui de l'entrée qu'on est en train
    // de rejouer (la nouvelle entrée courante).
    this._lastAppliedNavigationHint = nextEntry?.navigationHint ?? null;

    this.updateHistoryState({
      currentIndex: newIndex,
      canUndo: true,
      canRedo: newIndex < current.entries.length - 1
    });

    console.log(`Redo: "${nextEntry.description}"`);
    historyNavLog('redo: restored entry', {
      id: nextEntry.id,
      hint: nextEntry.navigationHint ?? null,
    });
    return this.deepClone(nextEntry.configuration);
  }

  // ─── Requêtes ─────────────────────────────────────────────

  /**
   * Récupère l'entrée actuellement active dans l'historique
   */
  getCurrentEntry(): HistoryEntry | null {
    const current = this.historyState;
    if (current.currentIndex >= 0 && current.currentIndex < current.entries.length) {
      return current.entries[current.currentIndex];
    }
    return null;
  }

  /**
   * Récupère les dernières entrées de l'historique
   * @param limit Nombre maximum d'entrées à retourner
   */
  getHistoryEntries(limit: number = 10): HistoryEntry[] {
    const current = this.historyState;
    const startIndex = Math.max(0, current.entries.length - limit);
    return current.entries.slice(startIndex).reverse();
  }

  /**
   * Récupère les statistiques de l'historique
   */
  getHistoryStats(): {
    totalEntries: number;
    currentPosition: number;
    autoSaves: number;
    manualSaves: number;
    batchSaves: number;
    totalSnapshotSize: number;
    oldestEntry: Date | null;
    newestEntry: Date | null;
  } {
    const current = this.historyState;
    const entries = current.entries;

    return {
      totalEntries: entries.length,
      currentPosition: current.currentIndex + 1,
      autoSaves: entries.filter(e => e.type === 'auto').length,
      manualSaves: entries.filter(e => e.type === 'manual').length,
      batchSaves: entries.filter(e => e.type === 'batch').length,
      totalSnapshotSize: entries.reduce((sum, e) => sum + (e.snapshotSize || 0), 0),
      oldestEntry: entries.length > 0 ? new Date(entries[0].timestamp) : null,
      newestEntry: entries.length > 0 ? new Date(entries[entries.length - 1].timestamp) : null
    };
  }

  // ─── Administration ───────────────────────────────────────

  /**
   * Efface complètement l'historique
   */
  clearHistory(): void {
    this._lastAppliedNavigationHint = null;
    this.updateHistoryState({
      entries: [],
      currentIndex: -1,
      canUndo: false,
      canRedo: false
    });

    console.log('Historique effacé');
  }

  /**
   * Définit la taille maximale de l'historique
   * @param size Nouvelle taille maximale (minimum 1)
   */
  setMaxHistorySize(size: number): void {
    if (size < 1) {
      throw new Error('La taille maximale de l\'historique doit être d\'au moins 1');
    }

    this._maxHistorySize = size;
    const current = this.historyState;
    
    if (current.entries.length > size) {
      const entries = current.entries.slice(-size);
      const newIndex = Math.min(current.currentIndex, entries.length - 1);
      
      this.updateHistoryState({
        entries,
        currentIndex: newIndex,
        maxHistorySize: size,
        canUndo: newIndex > 0,
        canRedo: newIndex < entries.length - 1
      });
    } else {
      this.updateHistoryState({ maxHistorySize: size });
    }
  }

  // ─── Gestion du flag fromHistory ──────────────────────────

  /**
   * Active/désactive le flag fromHistory avec un timer de sécurité.
   * Le flag est automatiquement désactivé après 2500ms pour éviter les blocages.
   */
  private setFromHistory(value: boolean): void {
    this._fromHistory = value;

    if (this._fromHistoryTimer) {
      clearTimeout(this._fromHistoryTimer);
      this._fromHistoryTimer = undefined;
    }

    if (value) {
      // Sécurité : désactiver automatiquement après un délai
      this._fromHistoryTimer = setTimeout(() => {
        this._fromHistory = false;
        this._fromHistoryTimer = undefined;
      }, 2500);
    }
  }

  /**
   * Force la désactivation du flag fromHistory.
   * Appelé par l'auto-save quand la sauvegarde post-undo/redo est terminée.
   */
  clearFromHistory(): void {
    if (this._fromHistoryTimer) {
      clearTimeout(this._fromHistoryTimer);
      this._fromHistoryTimer = undefined;
    }
    this._fromHistory = false;
  }

  // ─── Génération de descriptions ───────────────────────────

  /**
   * Génère une description automatique pour une entrée d'historique
   */
  private generateDescription(config: any, type: string): string {
    const time = new Date().toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
    const langCount = config?.configurations ? Object.keys(config.configurations).length : 
                      config?.baseConfigurations ? Object.keys(config.baseConfigurations).length : 0;
    
    switch (type) {
      case 'manual':
        return `Sauvegarde manuelle (${time})`;
      case 'auto':
        return `Modification auto (${time})`;
      case 'initial':
        return `Configuration initiale`;
      case 'batch':
        return `Opération groupée (${time})`;
      default:
        return `Modification (${time})`;
    }
  }

  /**
   * Génère une description lisible à partir d'un contexte de modification.
   * Exemples : "Ajout du code AJ614", "Modification indice EE632", "Suppression du jeu Quiz"
   */
  private generateContextDescription(context: HistoryChangeContext): string {
    const { action, entityType, entityId, field, language } = context;

    const entityLabels: Record<string, string> = {
      'code': 'code',
      'game': 'jeu',
      'quiz': 'quiz',
      'question': 'question',
      'structure': 'structure',
      'language': 'langue'
    };

    const actionLabels: Record<string, string> = {
      'add': 'Ajout',
      'remove': 'Suppression',
      'update': 'Modification',
      'reorder': 'Réorganisation'
    };

    const fieldLabels: Record<string, string> = {
      'hint': 'indice',
      'location': 'emplacement',
      'label': 'nom',
      'points': 'points',
      'status': 'statut',
      'constraints': 'contraintes',
      'gameType': 'type de jeu',
      'questions': 'questions',
      'answers': 'réponses',
      'orderIndex': 'ordre du parcours'
    };

    const actionStr = actionLabels[action || 'update'] || 'Modification';
    const entityStr = entityLabels[entityType || 'structure'] || entityType;

    let desc = actionStr;

    if (field) {
      desc += ` ${fieldLabels[field] || field}`;
      if (entityId) desc += ` ${entityId}`;
    } else if (entityType) {
      if (action === 'add') {
        desc += ` du ${entityStr}`;
      } else if (action === 'remove') {
        desc += ` du ${entityStr}`;
      } else {
        desc += ` ${entityStr}`;
      }
      if (entityId) desc += ` ${entityId}`;
    }

    if (language) {
      desc += ` (${language.toUpperCase()})`;
    }

    return desc;
  }

  // ─── Utilitaires privés ───────────────────────────────────

  /**
   * Génère un identifiant unique pour une entrée d'historique
   */
  private generateId(): string {
    return Date.now().toString(36) + Math.random().toString(36).substr(2);
  }

  /**
   * Effectue un clonage profond d'un objet
   */
  private deepClone<T>(obj: T): T {
    return JSON.parse(JSON.stringify(obj));
  }

  /**
   * Met à jour l'état de l'historique
   */
  private updateHistoryState(updates: Partial<HistoryState>): void {
    const current = this.historyState;
    this._historyState.next({ ...current, ...updates });
  }
}